import React from 'react';

import { useIntl } from 'ha/i18n';

import { RoomsValue, Filters } from 'ha/types/SearchFilters';
import { formatDateRange } from '@ha/date';

import { priceRange } from 'ha/utils/formatRanges';
import { DEFAULT_CURRENCY } from 'ha/constants/Currencies';
import { Chip, Grid } from '@ha/dsl';

import { getTagList } from './utils/getTagList';

interface Props {
  rate?: number;
  hideDateFilterTag?: boolean;
  filters: Filters;
  onRemove: (filter: keyof Filters, value?: string) => void;
}

const FiltersTags: React.FC<Props> = ({
  filters,
  rate = 1,
  onRemove,
  hideDateFilterTag = false,
}) => {
  const { T, formatDate } = useIntl();
  const { rooms, price, currency, dates, propertySize } = filters;

  const tagList = React.useMemo(() => getTagList(T, filters), [T, filters]);

  return (
    <Grid container spacing={1}>
      {tagList.map(({ category, value, label }, index) => (
        <Grid item key={index}>
          <Chip
            variant="outlined"
            onDelete={() => onRemove(category, value)}
            label={T(label)}
            data-test-locator={`FiltersTags ${value} Tag`}
          />
        </Grid>
      ))}

      {rooms?.bedroomCount.map((val, index) => (
        <Grid item key={index}>
          <Chip
            variant="outlined"
            onDelete={() => onRemove('rooms', val)}
            label={
              val === RoomsValue.ONE
                ? T('%s bedroom', val)
                : T('%s bedrooms', val)
            }
            data-test-locator="FiltersTags Rooms Tag"
          />
        </Grid>
      ))}

      {price && (price.min !== null || price.max !== null) && (
        <Grid item>
          <Chip
            variant="outlined"
            onDelete={() => onRemove('price')}
            label={priceRange(
              currency ?? DEFAULT_CURRENCY,
              price.min !== null ? Math.floor(price.min * rate) : null,
              price.max !== null ? Math.floor(price.max * rate) : null,
            )}
            data-test-locator="FiltersTags Price Tag"
          />
        </Grid>
      )}

      {!hideDateFilterTag && dates?.startDate && (
        <Grid item>
          <Chip
            variant="outlined"
            onDelete={() => onRemove('dates')}
            label={formatDateRange(
              formatDate,
              dates.startDate,
              dates.endDate || undefined,
            )}
            data-test-locator="FiltersTags Dates Tag"
          />
        </Grid>
      )}

      {propertySize && (!!propertySize.sizeMin || !!propertySize.sizeMax) && (
        <Grid item>
          <Chip
            variant="outlined"
            onDelete={() => onRemove('propertySize')}
            label={
              !propertySize.sizeMax
                ? `${propertySize.sizeMin} m² ${T('option.or_more')}`
                : `${propertySize.sizeMin || 0} m² - ${propertySize.sizeMax} m²`
            }
            data-test-locator="FiltersTags PropertySize Tag"
          />
        </Grid>
      )}
    </Grid>
  );
};

export default FiltersTags;
